import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
    FaSearch, FaFilter, FaExternalLinkAlt, FaCalendarAlt, 
    FaBuilding, FaGlobe, FaAward, FaChartBar 
} from 'react-icons/fa';
import Nav from '../components/Nav';
import grantService from '../services/grantService';
import { FeedSidebarMenu, FeedProfileCard } from '../components/feed';
import './Grants.css';

const GrantsPage = () => {
    const { user, token } = useAuth();
    const { t } = useTranslation();

    const [grants, setGrants] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [areaFilter, setAreaFilter] = useState('todas');
    const [page, setPage] = useState(1);
    const [totalGrants, setTotalGrants] = useState(0);

    useEffect(() => {
        loadGrants();
        loadStats();
    }, [page, areaFilter]);

    const loadGrants = async () => {
        try {
            setLoading(true);
            const data = await grantService.getGrants({
                query: searchQuery,
                area: areaFilter,
                page
            }, token);
            setGrants(data.grants);
            setTotalGrants(data.total);
        } catch (err) {
            console.error('Error loading grants:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await grantService.getStats(token);
            setStats(data);
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadGrants();
    };

    return (
        <div className="grants-page fd-page">
            <Nav />
            <div className="fd-layout">
                {/* ── COLUMNA IZQUIERDA ── */}
                <aside className="fd-left">
                    <FeedProfileCard user={user} />
                    <FeedSidebarMenu />
                    
                    {stats && (
                        <div className="grants-stats-card fd-sidebar-card">
                            <h3><FaChartBar /> Estadísticas</h3>
                            <div className="stat-item">
                                <span>Total Grants:</span>
                                <strong>{stats.total_grants}</strong>
                            </div>
                            <div className="stat-list">
                                <h4>Top Áreas</h4>
                                {Object.entries(stats.areas).map(([area, count]) => (
                                    <div key={area} className="stat-row">
                                        <span>{area}</span>
                                        <span>{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>

                {/* ── COLUMNA CENTRAL ── */}
                <main className="fd-center">
                    <header className="grants-header">
                        <h1>Explorador de Grants</h1>
                        <p>Busca convocatorias y financiamiento para investigación en salud a nivel global.</p>
                        
                        <form className="grants-search-bar" onSubmit={handleSearch}>
                            <div className="search-input-wrapper">
                                <FaSearch />
                                <input 
                                    type="text" 
                                    placeholder="Buscar por enfermedad, institución o país..." 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <select 
                                value={areaFilter}
                                onChange={(e) => setAreaFilter(e.target.value)}
                            >
                                <option value="todas">Todas las áreas</option>
                                <option value="salud">Salud</option>
                                <option value="investigacion">Investigación</option>
                                <option value="ambas">Ambas</option>
                            </select>
                            <button type="submit" className="btn-primary">Buscar</button>
                        </form>
                    </header>

                    <div className="grants-results">
                        {loading ? (
                            <div className="loading-state">Cargando oportunidades...</div>
                        ) : grants.length > 0 ? (
                            <div className="grants-list">
                                {grants.map(grant => (
                                    <div key={grant.id} className="grant-card">
                                        <div className="grant-card-header">
                                            <span className={`grant-badge ${grant.area}`}>
                                                {grant.area}
                                            </span>
                                            <span className="grant-date">
                                                <FaCalendarAlt /> {grant.fecha_limite || 'Ver link'}
                                            </span>
                                        </div>
                                        <h3>{grant.nombre}</h3>
                                        <div className="grant-info">
                                            <span><FaBuilding /> {grant.organizacion}</span>
                                            <span><FaGlobe /> {grant.pais_region}</span>
                                        </div>
                                        <p className="grant-desc">{grant.descripcion}</p>
                                        <div className="grant-card-footer">
                                            <span className="grant-monto">{grant.monto && `Monto: ${grant.monto}`}</span>
                                            <a href={grant.link} target="_blank" rel="noopener noreferrer" className="btn-outline">
                                                Ver Convocatoria <FaExternalLinkAlt />
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <h3>No se encontraron grants</h3>
                                <p>Prueba ajustando tus criterios de búsqueda.</p>
                            </div>
                        )}
                    </div>
                    
                    {totalGrants > 20 && (
                        <div className="pagination">
                            <button 
                                disabled={page === 1} 
                                onClick={() => setPage(page - 1)}
                            >Anterior</button>
                            <span>Página {page} de {Math.ceil(totalGrants / 20)}</span>
                            <button 
                                disabled={page >= Math.ceil(totalGrants / 20)} 
                                onClick={() => setPage(page + 1)}
                            >Siguiente</button>
                        </div>
                    )}
                </main>

                {/* ── COLUMNA DERECHA ── */}
                <aside className="fd-right">
                    <div className="grants-info-card fd-sidebar-card">
                        <h3><FaAward /> ¿Por qué buscar aquí?</h3>
                        <p>Nuestro sistema recolecta diariamente convocatorias de más de 400 fuentes internacionales, incluyendo NIH, Minciencias, Wellcome Trust y más.</p>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default GrantsPage;
